import { Pencil } from "lucide-react";
import type { CSSProperties } from "react";
import type { PoiCategory } from "../lib/pois.ts";

/**
 * Layer 2's category chips. The heat and the pins are drawn by the map from the collection the
 * page hands it; this is the row that narrows that collection to one category, because "we are
 * hungry" is a question a heatmap of everything cannot answer.
 *
 * A row along the top, where layer 1's day tabs sit, so it is under a thumb while walking. It
 * scrolls sideways rather than wrapping when a trip has added more categories than fit, so the
 * map underneath is never covered by a second row.
 *
 * A category's chip is its emoji alone, named for a screen reader by the category's name; picked,
 * it is outlined in the category's colour, the colour its pins wear. "All" stays a word. A place
 * with no category shows under "All" only: there is no chip for none. The row ends with the way
 * to the Places page's Categories tab.
 */
export function PoiLayer({
  categories,
  active,
  onCategory,
  onEditCategories,
}: {
  categories: PoiCategory[];
  /** The category the heat is narrowed to; null is every category. */
  active: string | null;
  onCategory: (id: string | null) => void;
  /** Opens the Places page on its Categories tab; absent, the row offers no way in. */
  onEditCategories?: (() => void) | undefined;
}) {
  return (
    <div className="poi-chips" role="group" aria-label="Categories">
      <button type="button" aria-pressed={active === null} onClick={() => onCategory(null)}>
        All
      </button>
      {categories.map((c) => (
        <button
          key={c.id}
          type="button"
          className="poi-chip-emoji"
          aria-label={c.name}
          title={c.name}
          aria-pressed={c.id === active}
          style={{ "--chip-color": c.color } as CSSProperties}
          // Tapping the one already picked goes back to everything, as a toggle is expected to.
          onClick={() => onCategory(c.id === active ? null : c.id)}
        >
          {c.emoji}
        </button>
      ))}
      {onEditCategories && (
        <button
          type="button"
          className="poi-chip-edit"
          aria-label="Edit categories"
          title="Edit categories"
          onClick={onEditCategories}
        >
          <Pencil size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
